import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import * as moment from "moment";
import { Subscription } from "rxjs";
import { APIService } from "src/app/api/api.service";
import { isNhaCompany } from "src/app/dash/dash.component";
import { NotifierService } from "src/app/_services/notifier.service";
import { environment } from "src/environments/environment";
import {
  SUPPORT_DASHBOARD_DETAIL_CREATION_TIME_TABLE_HEADERS,
  SUPPORT_DASHBOARD_DETAIL_FILTER_KEYS,
  SUPPORT_DASHBOARD_DETAIL_METRIC_TITLES,
  SUPPORT_DASHBOARD_DETAIL_TABLE_HEADERS,
} from "./support-dashboard-detail.constants";
import {
  CreationTimePeriod,
  DetailedTicketListIssue,
  FilterValuesResponse,
  JiraContext,
  SlaState,
  SupportDashboardDetailMetric,
  SupportTicketFieldKey,
  SupportTicketFilterKey,
  SupportTicketTableHeader,
  TicketListIssue,
  TicketListRequestPayload,
  TicketListResponse,
  TicketSlaExceptionUpdateRequestPayload,
  TicketSlaExceptionUpdateResponse,
} from "./support-dashboard-detail.types";
import {
  getErrorMessage,
  isSuccessResponse,
  isValidDate,
  normalizeProjectKeys,
  normalizeResponse,
  parseReporterQueryValue,
} from "./support-dashboard-detail.utils";

declare let flatpickr: any;

@Component({
  selector: "app-support-dashboard-detail",
  templateUrl: "./support-dashboard-detail.component.html",
  styleUrls: [
    "./support-dashboard-detail.component.css",
    "../../ticket/ticket.component.css",
  ],
})
export class SupportDashboardDetailComponent implements OnInit, OnDestroy {
  readonly backRoute = ["/dash/dashboard"];
  readonly defaultTableHeaders: SupportTicketTableHeader[] =
    SUPPORT_DASHBOARD_DETAIL_TABLE_HEADERS;
  readonly creationTimeTableHeaders: SupportTicketTableHeader[] =
    SUPPORT_DASHBOARD_DETAIL_CREATION_TIME_TABLE_HEADERS;
  readonly filterKeys = SUPPORT_DASHBOARD_DETAIL_FILTER_KEYS;

  accountId = localStorage.getItem("accountId") || "";
  urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  metric: SupportDashboardDetailMetric = "total-tickets";
  startDate = moment().startOf("month").format("YYYY-MM-DD");
  endDate = moment().format("YYYY-MM-DD");
  dateField = "created";
  includeResolved = true;
  dateRange = "";
  perPage = 10;
  perPageOptions = [10, 50, 100, 200];
  reporterOptions: Array<{ id: string; name: string; ticketCount: number }> = [];
  selectedReporter = "";
  loadingReporters = false;
  selectedOrganization = "";
  selectedAssignees: string[] = [];
  selectedPriority = "";
  selectedSlaName: "" | "time_to_resolution" | "time_to_first_response" = "";
  selectedSlaState: "" | "met" | "breached" = "";
  exceptionStatus: "met" | "breached" = "met";
  exceptionReason = "";
  exceptionReasonError: string | null = null;
  breachedReason = "";
  breachedReasonError: string | null = null;
  currentPage = 1;
  filterKey: SupportTicketFilterKey = this.filterKeys[0].id;
  filterValue = "";
  priorityValues: Array<{ id: string; name: string }> = [];
  tickets: TicketListIssue[] = [];
  totalTickets = 0;
  nextToken: string | null = null;
  loadingTickets = false;
  loadingSearchValues = false;
  updatingTicketId: string | null = null;
  errorMessage: string | null = null;
  confirmationTicket: TicketListIssue | null = null;
  exceptionFormTicket: TicketListIssue | null = null;
  breachedConfirmationTicket: TicketListIssue | null = null;
  breachedExceptionFormTicket: TicketListIssue | null = null;
  currentMessage: Subscription | null = null;
  activeTicket: any = null;
  pageCache: { [pageNumber: number]: TicketListIssue[] } = {};
  pageRequestTokens: { [pageNumber: number]: string | null } = { 1: null };
  pageNextTokens: { [pageNumber: number]: string | null } = {};
  backQueryParams: {
    startDate?: string;
    endDate?: string;
    dateField?: string;
    includeResolved?: boolean;
    organization?: string;
    assignees?: string;
    reporter?: string;
    priority?: string;
  } = {};

  private readonly metricTitles = SUPPORT_DASHBOARD_DETAIL_METRIC_TITLES;

  constructor(
    private apiService: APIService,
    private notifier: NotifierService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.metric = this.normalizeMetric(this.route.snapshot.paramMap.get("metric"));
    this.initializeRouteFilters();
    this.syncDateRangeDisplay();
    this.updateBackQueryParams();
    this.subscribeToAccountChanges();
    this.initializeDateRangePicker();
    if (this.usesDropdownFilter) {
      void this.checkForValues();
    }
    void this.loadReporterOptions();
    void this.loadTickets();
  }

  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  get metricTitle(): string {
    if (
      this.metric === "sla-priority-details" &&
      this.selectedPriority &&
      this.selectedSlaName &&
      this.selectedSlaState
    ) {
      return `${this.getSlaDisplayName(this.selectedSlaName)} - ${
        this.selectedSlaState === "met" ? "Met" : "Breached"
      } - ${this.selectedPriority}`;
    }

    if (this.metric === "priority-details" && this.selectedPriority) {
      return `${this.metricTitles[this.metric]} - ${this.selectedPriority}`;
    }

    return this.metricTitles[this.metric];
  }

  get tableHeaders(): SupportTicketTableHeader[] {
    return this.usesDetailedTicketTable
      ? this.creationTimeTableHeaders
      : this.defaultTableHeaders;
  }

  get displayedTickets(): number {
    return this.tickets.length;
  }

  get hasTickets(): boolean {
    return this.tickets.length > 0;
  }

  get isCreationTimeMetric(): boolean {
    return (
      this.metric === "business-hours" || this.metric === "after-hours"
    );
  }

  get isPriorityDetailMetric(): boolean {
    return this.metric === "priority-details";
  }

  get isSlaPriorityDetailMetric(): boolean {
    return this.metric === "sla-priority-details";
  }

  get usesDetailedTicketTable(): boolean {
    return (
      this.isCreationTimeMetric ||
      this.isPriorityDetailMetric ||
      this.isSlaPriorityDetailMetric
    );
  }

  get showMarkAsMetAction(): boolean {
    if (this.hideActionColumnForNhaClient()) {
      return false;
    }

    return (
      this.metric === "resolution-breached" ||
      this.metric === "response-breached"
    );
  }

  get showMarkAsBreachedAction(): boolean {
    if (this.hideActionColumnForNhaClient()) {
      return false;
    }

    return (
      this.metric === "resolution-met" || this.metric === "response-met"
    );
  }

  get tableColumnCount(): number {
    return (
      this.tableHeaders.length +
      1 +
      (this.showMarkAsMetAction ? 1 : 0) +
      (this.showMarkAsBreachedAction ? 1 : 0)
    );
  }

  get usesDropdownFilter(): boolean {
    return this.filterKey === "priority";
  }

  get showReporterFilter(): boolean {
    return this.metric === "total-tickets";
  }

  private isNhaClientUser(): boolean {
    return this.urlPrefix === "client" && isNhaCompany();
  }

  private hideActionColumnForNhaClient(): boolean {
    return (
      this.isNhaClientUser() &&
      (this.metric === "resolution-met" ||
        this.metric === "resolution-breached" ||
        this.metric === "response-met" ||
        this.metric === "response-breached")
    );
  }

  get usesPageNumberPagination(): boolean {
    return this.isSlaPriorityDetailMetric;
  }

  get totalPages(): number {
    const totalPages = Math.ceil(this.totalTickets / this.perPage);
    return totalPages > 0 ? totalPages : 1;
  }

  get canGoPrevious(): boolean {
    return this.currentPage > 1 && !this.loadingTickets;
  }

  get canGoNext(): boolean {
    if (this.usesPageNumberPagination) {
      return !this.loadingTickets && this.currentPage < this.totalPages;
    }

    return (
      !this.loadingTickets &&
      this.currentPage < this.totalPages &&
      Boolean(
        this.pageCache[this.currentPage + 1] ||
          this.pageNextTokens[this.currentPage]
      )
    );
  }

  async refresh(): Promise<void> {
    this.resetPaginationState();
    this.updateBackQueryParams();
    await this.loadReporterOptions();
    await this.loadTickets(1);
  }

  async goToPreviousPage(): Promise<void> {
    if (!this.canGoPrevious) {
      return;
    }

    await this.loadTickets(this.currentPage - 1);
  }

  async goToNextPage(): Promise<void> {
    if (!this.canGoNext) {
      return;
    }

    await this.loadTickets(this.currentPage + 1);
  }

  async onPerPageChange(): Promise<void> {
    await this.refresh();
  }

  async clearFilters(): Promise<void> {
    this.filterValue = "";
    await this.refresh();
  }

  async onFilterKeyChange(): Promise<void> {
    this.filterValue = "";
    this.priorityValues = [];
    if (this.usesDropdownFilter) {
      await this.checkForValues();
    }
  }

  getSerialNumber(index: number): number {
    return (this.currentPage - 1) * this.perPage + index + 1;
  }

  getTicketDisplayValue(
    ticket: TicketListIssue,
    field: SupportTicketFieldKey
  ): string {
    const value = ticket[field];
    return typeof value === "string" && value.trim() ? value : "-";
  }

  async showTicketDetails(ticket: TicketListIssue): Promise<void> {
    this.activeTicket = {
      issue_key: ticket.ticket_id || "",
      summary: ticket.ticket_summary || "",
      status: ticket.status || "",
      priority: ticket.priority || "",
      created: ticket.created_on || "",
      creator: ticket.reporter || "",
      reporter: ticket.reporter || "",
      issue_type: ticket.issue_type || "",
      assignee: ticket.assignee || "",
      resolved_time: ticket.closed_on || "",
      resolved:
        Boolean(ticket.closed_on) ||
        ["resolved", "closed", "completed"].some((state) =>
          (ticket.status || "").toLowerCase().includes(state)
        ),
      timeline: false,
    };
  }

  goBackToTicketList(): void {
    this.activeTicket = null;
  }

  async onReporterChange(selectedValue: unknown): Promise<void> {
    if (!this.showReporterFilter) {
      return;
    }

    if (typeof selectedValue === "string") {
      this.selectedReporter = selectedValue;
    } else if (
      selectedValue &&
      typeof selectedValue === "object" &&
      "id" in (selectedValue as { id?: unknown }) &&
      typeof (selectedValue as { id: unknown }).id === "string"
    ) {
      this.selectedReporter = (selectedValue as { id: string }).id;
    } else {
      this.selectedReporter = "";
    }

    await this.refresh();
  }

  markAsMet(ticket: TicketListIssue): void {
    if (!this.showMarkAsMetAction || this.updatingTicketId) {
      return;
    }

    this.resetExceptionForm();
    this.resetBreachedExceptionForm();
    this.confirmationTicket = ticket;
  }

  closeMarkAsMetConfirmation(): void {
    if (this.updatingTicketId) {
      return;
    }

    this.confirmationTicket = null;
  }

  confirmMarkAsMet(): void {
    const ticket = this.confirmationTicket;
    if (!ticket || !this.showMarkAsMetAction) {
      return;
    }

    if (this.updatingTicketId) {
      return;
    }

    this.confirmationTicket = null;
    this.exceptionFormTicket = ticket;
    this.exceptionStatus = "met";
    this.exceptionReason = "";
    this.exceptionReasonError = null;
  }

  closeExceptionForm(): void {
    if (this.updatingTicketId) {
      return;
    }

    this.resetExceptionForm();
  }

  canShowMarkAsBreached(ticket: TicketListIssue): boolean {
    if (this.metric === "resolution-met") {
      return (
        (ticket.exception_resolution_status || "").toLowerCase() === "met"
      );
    }

    if (this.metric === "response-met") {
      return (
        (ticket.exception_response_status || "").toLowerCase() === "met"
      );
    }

    return false;
  }

  markAsBreached(ticket: TicketListIssue): void {
    if (
      !this.showMarkAsBreachedAction ||
      !this.canShowMarkAsBreached(ticket) ||
      this.updatingTicketId
    ) {
      return;
    }

    this.resetExceptionForm();
    this.resetBreachedExceptionForm();
    this.breachedConfirmationTicket = ticket;
  }

  closeMarkAsBreachedConfirmation(): void {
    if (this.updatingTicketId) {
      return;
    }

    this.breachedConfirmationTicket = null;
  }

  confirmMarkAsBreached(): void {
    const ticket = this.breachedConfirmationTicket;
    if (!ticket || !this.canShowMarkAsBreached(ticket)) {
      return;
    }

    if (this.updatingTicketId) {
      return;
    }

    this.breachedConfirmationTicket = null;
    this.breachedExceptionFormTicket = ticket;
    this.breachedReason = "";
    this.breachedReasonError = null;
  }

  closeBreachedExceptionForm(): void {
    if (this.updatingTicketId) {
      return;
    }

    this.resetBreachedExceptionForm();
  }

  async submitExceptionForm(): Promise<void> {
    const ticket = this.exceptionFormTicket;
    if (!ticket || !this.showMarkAsMetAction) {
      return;
    }

    const trimmedReason = this.exceptionReason.trim();
    if (!trimmedReason) {
      this.exceptionReasonError = "Reason is required.";
      return;
    }

    await this.submitTicketExceptionUpdate(
      ticket,
      "met",
      trimmedReason,
      "Ticket marked as met successfully.",
      () => {
        this.confirmationTicket = null;
        this.resetExceptionForm();
      }
    );
  }

  async submitBreachedExceptionForm(): Promise<void> {
    const ticket = this.breachedExceptionFormTicket;
    if (!ticket || !this.canShowMarkAsBreached(ticket)) {
      return;
    }

    const trimmedReason = this.breachedReason.trim();
    if (!trimmedReason) {
      this.breachedReasonError = "Reason is required.";
      return;
    }

    await this.submitTicketExceptionUpdate(
      ticket,
      "breached",
      trimmedReason,
      "Ticket marked as breached successfully.",
      () => {
        this.breachedConfirmationTicket = null;
        this.resetBreachedExceptionForm();
      }
    );
  }

  private async submitTicketExceptionUpdate(
    ticket: TicketListIssue,
    status: "met" | "breached",
    reason: string,
    successMessage: string,
    onSuccess: () => void
  ): Promise<void> {
    const jiraContext = this.getJiraContext();
    if (!jiraContext) {
      this.notifier.alert(
        "Info",
        "",
        "Managed Services support via SWAYAM is not enabled for your account. Please contact the admin.",
        "info",
        5000
      );
      return;
    }

    this.exceptionReasonError = null;
    this.breachedReasonError = null;
    const payload = this.buildTicketSlaExceptionPayload(
      jiraContext,
      ticket,
      status,
      reason
    );
    this.updatingTicketId = ticket.ticket_id;

    try {
      const result = await this.apiService.postDataPromis(
        this.getSupportJiraApiUrl(
          this.buildTicketSlaExceptionQueryString(payload)
        ),
        {},
        this.getRequestHeaders()
      );
      const response =
        normalizeResponse<TicketSlaExceptionUpdateResponse>(result);

      if (isSuccessResponse(response)) {
        onSuccess();
        this.notifier.alert(
          "Success",
          "",
          successMessage,
          "success",
          5000
        );
        await this.refresh();
        await this.router.navigate(this.backRoute, {
          queryParams: this.backQueryParams,
        });
      } else {
        this.notifier.alert(
          "Error",
          "",
          response?.error_message ||
            response?.message ||
            "Unable to update ticket SLA exception.",
          "error",
          5000
        );
      }
    } catch (error) {
      this.notifier.alert(
        "Error",
        "",
        getErrorMessage(error, "Unable to update ticket SLA exception."),
        "error",
        5000
      );
    } finally {
      this.updatingTicketId = null;
    }
  }

  private async loadTickets(pageNumber = 1): Promise<void> {
    if (!this.isDateRangeValid()) {
      return;
    }

    if (this.isPriorityDetailMetric && !this.selectedPriority.trim()) {
      this.tickets = [];
      this.totalTickets = 0;
      this.nextToken = null;
      this.errorMessage = "Priority filter is missing.";
      return;
    }

    if (
      this.isSlaPriorityDetailMetric &&
      (!this.selectedPriority.trim() ||
        !this.selectedSlaName ||
        !this.selectedSlaState)
    ) {
      this.tickets = [];
      this.totalTickets = 0;
      this.nextToken = null;
      this.errorMessage = "SLA priority detail filters are missing.";
      return;
    }

    const jiraContext = this.getJiraContext();
    if (!jiraContext) {
      this.tickets = [];
      this.totalTickets = 0;
      this.nextToken = null;
      this.errorMessage = null;
      this.notifier.alert(
        "Info",
        "",
        "Managed Services support via SWAYAM is not enabled for your account. Please contact the admin.",
        "info",
        5000
      );
      return;
    }

    if (this.pageCache[pageNumber]) {
      this.currentPage = pageNumber;
      this.tickets = this.pageCache[pageNumber] || [];
      this.nextToken = this.pageNextTokens[pageNumber] || null;
      return;
    }

    const requestToken = this.pageRequestTokens[pageNumber];
    if (
      pageNumber > 1 &&
      !this.usesPageNumberPagination &&
      requestToken === undefined
    ) {
      return;
    }

    const payload = this.buildTicketListPayload(
      jiraContext,
      pageNumber,
      requestToken || undefined
    );

    this.loadingTickets = true;
    this.errorMessage = null;

    try {
      const result = await this.apiService.postDataPromis(
        this.getSupportJiraApiUrl(this.buildTicketListQueryString(payload)),
        {},
        this.getRequestHeaders()
      );
      if (this.usesDetailedTicketTable) {
        const response = normalizeResponse<
          TicketListResponse<DetailedTicketListIssue>
        >(result);

        if (isSuccessResponse(response)) {
          this.applyTicketResponse(pageNumber, response, (issue) => {
            return this.mapDetailedTicketIssue(issue);
          });
        } else {
          this.errorMessage =
            response?.error_message ||
            response?.message ||
            "Unable to load support tickets.";
        }
      } else {
        const response = normalizeResponse<TicketListResponse>(result);

        if (isSuccessResponse(response)) {
          this.applyTicketResponse(pageNumber, response, (issue) => {
            return this.mapStandardTicketIssue(issue);
          });
        } else {
          this.errorMessage =
            response?.error_message ||
            response?.message ||
            "Unable to load support tickets.";
        }
      }
    } catch (error) {
      this.errorMessage = getErrorMessage(
        error,
        "Unable to load support tickets."
      );
    } finally {
      this.loadingTickets = false;
    }
  }

  private async checkForValues(): Promise<void> {
    if (!this.usesDropdownFilter) {
      return;
    }

    const jiraContext = this.getJiraContext();
    if (!jiraContext) {
      this.priorityValues = [];
      return;
    }

    this.loadingSearchValues = true;

    try {
      const queryString = this.buildFilterValuesQueryString(
        jiraContext,
        this.filterKey
      );
      const result = await this.apiService.postDataPromis(
        this.getSupportJiraApiUrl(queryString),
        {},
        this.getRequestHeaders()
      );
      const response = normalizeResponse<FilterValuesResponse>(result);

      if (isSuccessResponse(response)) {
        this.priorityValues = (response?.filter_values || []).map((value) => {
          return { id: value, name: value };
        });
      } else {
        this.priorityValues = [];
      }
    } catch {
      this.priorityValues = [];
    } finally {
      this.loadingSearchValues = false;
    }
  }

  private applyTicketResponse<TIssue>(
    pageNumber: number,
    response: TicketListResponse<TIssue> | null,
    mapIssue: (issue: TIssue) => TicketListIssue
  ): void {
    const issues = Array.isArray(response?.issues)
      ? response.issues.map((issue) => mapIssue(issue))
      : [];
    const nextToken =
      response?.next_token != null ? String(response.next_token) : null;

    this.currentPage = pageNumber;
    this.tickets = issues;
    this.totalTickets =
      Number(response?.total_count) ||
      Number(response?.all_issues) ||
      Number(response?.total) ||
      this.tickets.length;
    this.nextToken = nextToken;
    this.pageCache[pageNumber] = issues;
    this.pageNextTokens[pageNumber] = nextToken;

    if (nextToken) {
      this.pageRequestTokens[pageNumber + 1] = nextToken;
    }
  }

  private mapStandardTicketIssue(ticket: TicketListIssue): TicketListIssue {
    return {
      ticket_id: ticket?.ticket_id || "",
      ticket_summary: ticket?.ticket_summary || "",
      created_on: ticket?.created_on || "",
      reporter: ticket?.reporter || "",
      priority: ticket?.priority || "",
      status: ticket?.status || "",
      closed_on: ticket?.closed_on || "",
      issue_type: ticket?.issue_type || "",
      updated_on: ticket?.updated_on || "",
      assignee: ticket?.assignee || "",
      organisation: ticket?.organisation || "",
      exception_resolution_status: ticket?.exception_resolution_status || "",
      exception_response_status: ticket?.exception_response_status || "",
      exception_resolution_update_reason:
        ticket?.exception_resolution_update_reason || "",
      exception_response_update_reason:
        ticket?.exception_response_update_reason || "",
      exception_updated_at: ticket?.exception_updated_at || "",
    };
  }

  private async loadReporterOptions(): Promise<void> {
    if (!this.showReporterFilter) {
      this.reporterOptions = [];
      this.selectedReporter = "";
      this.loadingReporters = false;
      return;
    }

    const jiraContext = this.getJiraContext();
    if (!jiraContext) {
      this.reporterOptions = [];
      return;
    }

    this.loadingReporters = true;

    try {
      const result = await this.apiService.postDataPromis(
        this.getSupportReporterApiUrl(jiraContext),
        {},
        this.getRequestHeaders()
      );
      const response = normalizeResponse<{
        organization?: string;
        reporters?: Array<{ reporter_name: string; ticket_count: number }>;
        status?: string | number;
        s?: string | number;
        message?: string;
        error_message?: string;
      }>(result);

      if (isSuccessResponse(response)) {
        const mappedOptions = (response?.reporters || [])
          .map((reporter) => {
            const reporterName = (reporter?.reporter_name || "").trim();
            if (!reporterName) {
              return null;
            }

            return {
              id: reporterName,
              name: reporterName,
              ticketCount: Number(reporter?.ticket_count) || 0,
            };
          })
          .filter(
            (reporter): reporter is { id: string; name: string; ticketCount: number } =>
              reporter !== null
          );

        if (
          this.selectedReporter &&
          !mappedOptions.some((option) => option.id === this.selectedReporter)
        ) {
          mappedOptions.unshift({
            id: this.selectedReporter,
            name: this.selectedReporter,
            ticketCount: 0,
          });
        }

        this.reporterOptions = mappedOptions;
      } else {
        this.reporterOptions = [];
      }
    } catch {
      this.reporterOptions = [];
    } finally {
      this.loadingReporters = false;
    }
  }

  private getSupportReporterApiUrl(jiraContext: JiraContext): string {
    const params = new URLSearchParams();

    params.set("action", "list_reporters_by_organization");
    params.set("jira_project_key", JSON.stringify(jiraContext.jiraProjectKeys));
    params.set("start_date", this.startDate);
    params.set("end_date", this.endDate);
    params.set("date_field", this.dateField);
    params.set("include_resolved", String(this.includeResolved));

    if (this.selectedOrganization.trim()) {
      params.set("jira_organization_key", this.selectedOrganization.trim());
    }

    return `${environment.apiURL}/${this.urlPrefix}/apis/support/jira?${params.toString()}`;
  }

  private mapDetailedTicketIssue(ticket: DetailedTicketListIssue): TicketListIssue {
    return {
      ticket_id: ticket?.ticket_id || "",
      ticket_summary: ticket?.summary || "",
      created_on: ticket?.created || "",
      reporter: ticket?.reporter_name || "",
      priority: ticket?.priority || "",
      status: ticket?.status || "",
      closed_on: "",
      issue_type: ticket?.issue_type || "",
      updated_on: ticket?.updated || "",
      assignee: ticket?.assignee || "",
      organisation: ticket?.organisation || "",
    };
  }

  private initializeDateRangePicker(): void {
    setTimeout(() => {
      flatpickr(".datetimepicker", {
        dateFormat: "d-M-Y",
        mode: "range",
        maxDate: new Date(),
        showMonths: 2,
        onChange: async (selectedDates: Date[], dateStr: string) => {
          if (!dateStr.includes("to")) {
            return;
          }

          this.dateRange = dateStr;
          this.updateDateRangeValues(dateStr);
          await this.refresh();
        },
      });
    }, 100);
  }

  private initializeRouteFilters(): void {
    const queryParams = this.route.snapshot.queryParamMap;
    const startDate = queryParams.get("startDate");
    const endDate = queryParams.get("endDate");
    const dateField = queryParams.get("dateField");
    const includeResolved = queryParams.get("includeResolved");
    const organization = queryParams.get("organization");
    const assignees = queryParams.get("assignees");
    const reporter = queryParams.get("reporter");
    const reporters = queryParams.get("reporters");
    const priority = queryParams.get("priority");
    const slaName = queryParams.get("slaName");
    const slaState = queryParams.get("slaState");

    if (isValidDate(startDate)) {
      this.startDate = startDate;
    }

    if (isValidDate(endDate)) {
      this.endDate = endDate;
    }

    if (dateField) {
      this.dateField = dateField;
    }

    if (includeResolved != null) {
      this.includeResolved = includeResolved === "true";
    }

    this.selectedOrganization = (organization || "").trim();
    this.selectedAssignees = parseReporterQueryValue(assignees);
    const legacyReporters = parseReporterQueryValue(reporters);
    this.selectedReporter = this.showReporterFilter
      ? (reporter || "").trim() || legacyReporters[0] || ""
      : "";
    this.selectedPriority = (priority || "").trim();
    this.selectedSlaName =
      slaName === "time_to_resolution" || slaName === "time_to_first_response"
        ? slaName
        : "";
    this.selectedSlaState =
      slaState === "met" || slaState === "breached" ? slaState : "";
  }

  private updateDateRangeValues(dateRange: string): void {
    if (!(dateRange && dateRange.includes("to"))) {
      return;
    }

    const selectedDateRange = dateRange.toString().split(" to ");
    this.startDate = moment(selectedDateRange[0], "DD-MMM-YYYY").format(
      "YYYY-MM-DD"
    );
    this.endDate = moment(selectedDateRange[1], "DD-MMM-YYYY").format(
      "YYYY-MM-DD"
    );
    this.updateBackQueryParams();
  }

  private syncDateRangeDisplay(): void {
    this.dateRange =
      moment(this.startDate, "YYYY-MM-DD").format("DD-MMM-YYYY") +
      " to " +
      moment(this.endDate, "YYYY-MM-DD").format("DD-MMM-YYYY");
  }

  private updateBackQueryParams(): void {
    this.backQueryParams = {
      startDate: this.startDate || undefined,
      endDate: this.endDate || undefined,
      dateField: this.dateField || undefined,
      includeResolved: this.includeResolved,
      organization: this.selectedOrganization || undefined,
      assignees: this.selectedAssignees.length
        ? JSON.stringify(this.selectedAssignees)
        : undefined,
      reporter:
        this.showReporterFilter && this.selectedReporter
          ? this.selectedReporter
          : undefined,
      priority: this.isPriorityDetailMetric
        ? this.selectedPriority || undefined
        : undefined,
    };
  }

  private subscribeToAccountChanges(): void {
    let ignoreInitialReplay = true;

    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (ignoreInitialReplay) {
        ignoreInitialReplay = false;
        return;
      }

      if (!msg) {
        return;
      }

      let notificationData: any;
      try {
        notificationData = JSON.parse(msg);
      } catch {
        return;
      }

      if (
        notificationData?.value == null ||
        notificationData?.source !== "notifier"
      ) {
        return;
      }

      if (notificationData.key === "accountId") {
        this.accountId = notificationData.value;
        void this.refresh();
      }
    });
  }

  private buildTicketListPayload(
    jiraContext: JiraContext,
    pageNumber = 1,
    nextToken?: string
  ): TicketListRequestPayload {
    const metricFilter = this.getMetricRequestConfig();
    const payload: TicketListRequestPayload = {
      action: metricFilter.action,
      jira_project_key: jiraContext.jiraProjectKeys,
      start_date: this.startDate,
      end_date: this.endDate,
      date_field: this.dateField,
      include_resolved: this.includeResolved,
      page_size: this.perPage,
      sort_by: "created",
      sort_order: "desc",
    };

    if (this.selectedOrganization.trim()) {
      payload.jira_organization_key = this.selectedOrganization.trim();
    }

    if (this.usesPageNumberPagination) {
      payload.page_number = pageNumber;
    } else if (nextToken) {
      payload.next_token = nextToken;
    }

    const trimmedFilterValue = this.filterValue.trim();
    if (trimmedFilterValue) {
      if (this.filterKey === "summary") {
        payload.search_text = trimmedFilterValue;
      } else if (this.filterKey === "issuekey") {
        payload.ticket_ids = [trimmedFilterValue];
      } else if (this.filterKey === "priority") {
        payload.priorities = [trimmedFilterValue];
      }
    }

    if (this.selectedAssignees.length) {
      payload.assignees = [...this.selectedAssignees];
    }

    if (
      this.showReporterFilter &&
      this.selectedReporter.trim() &&
      payload.action === "ticket_list"
    ) {
      payload.reporters = [this.selectedReporter.trim()];
    }

    if (metricFilter.sla_name) {
      payload.sla_name = metricFilter.sla_name;
      payload.sla_state = metricFilter.sla_state;
    }

    if (metricFilter.time_period) {
      payload.time_period = metricFilter.time_period;
    }

    if (metricFilter.priority && metricFilter.priority.trim()) {
      payload.priorities = [metricFilter.priority.trim()];
    }

    return payload;
  }

  private getMetricRequestConfig(): {
    action:
      | "ticket_list"
      | "ticket_list_by_sla"
      | "ticket_details_by_creation_time"
      | "ticket_details_by_priority"
      | "ticket_details_by_sla_priority";
    sla_name?: string;
    sla_state?: SlaState;
    time_period?: CreationTimePeriod;
    priority?: string;
  } {
    switch (this.metric) {
      case "priority-details":
        return {
          action: "ticket_details_by_priority",
          priority: this.selectedPriority,
        };
      case "sla-priority-details":
        return {
          action: "ticket_details_by_sla_priority",
          priority: this.selectedPriority,
          sla_name: this.selectedSlaName || undefined,
          sla_state: this.selectedSlaState || undefined,
        };
      case "business-hours":
        return {
          action: "ticket_details_by_creation_time",
          time_period: "business_hours",
        };
      case "after-hours":
        return {
          action: "ticket_details_by_creation_time",
          time_period: "non_business_hours",
        };
      case "resolution-met":
        return {
          action: "ticket_list_by_sla",
          sla_name: "time_to_resolution",
          sla_state: "met",
        };
      case "resolution-breached":
        return {
          action: "ticket_list_by_sla",
          sla_name: "time_to_resolution",
          sla_state: "breached",
        };
      case "response-met":
        return {
          action: "ticket_list_by_sla",
          sla_name: "time_to_first_response",
          sla_state: "met",
        };
      case "response-breached":
        return {
          action: "ticket_list_by_sla",
          sla_name: "time_to_first_response",
          sla_state: "breached",
        };
      default:
        return { action: "ticket_list" };
    }
  }

  private buildTicketListQueryString(
    payload: TicketListRequestPayload
  ): string {
    const params = new URLSearchParams();

    params.set("action", payload.action);
    params.set("jira_project_key", JSON.stringify(payload.jira_project_key));
    params.set("start_date", payload.start_date);
    params.set("end_date", payload.end_date);

    if (payload.jira_organization_key?.trim()) {
      params.set("jira_organization_key", payload.jira_organization_key);
    }

    if (payload.assignees?.length) {
      params.set("assignees", JSON.stringify(payload.assignees));
    }

    if (payload.reporters?.length) {
      params.set("reporters", JSON.stringify(payload.reporters));
    }

    if (payload.filters && Object.keys(payload.filters).length) {
      params.set("filters", JSON.stringify(payload.filters));
    }

    params.set("include_resolved", String(payload.include_resolved));
    params.set("date_field", payload.date_field);
    params.set("page_size", String(payload.page_size));
    params.set("sort_by", payload.sort_by);
    params.set("sort_order", payload.sort_order);

    if (payload.page_number != null) {
      params.set("page_number", String(payload.page_number));
    }

    if (payload.next_token) {
      params.set("next_token", payload.next_token);
    }

    if (payload.search_text) {
      params.set("search_text", payload.search_text);
    }

    if (payload.ticket_ids?.length) {
      params.set("ticket_ids", JSON.stringify(payload.ticket_ids));
    }

    if (payload.priorities?.length) {
      params.set(
        "priorities",
        payload.action === "ticket_details_by_priority"
          ? payload.priorities[0]
          : JSON.stringify(payload.priorities)
      );
    }

    if (payload.sla_name) {
      params.set("sla_name", payload.sla_name);
    }

    if (payload.sla_state) {
      params.set("sla_state", payload.sla_state);
    }

    if (payload.time_period) {
      params.set("time_period", payload.time_period);
    }

    return params.toString();
  }

  private buildFilterValuesQueryString(
    jiraContext: JiraContext,
    filterName: string
  ): string {
    const params = new URLSearchParams();

    params.set("action", "jira_filter_values");
    params.set("filter_name", filterName);
    params.set("jira_project_key", JSON.stringify(jiraContext.jiraProjectKeys));

    if (this.selectedOrganization.trim()) {
      params.set("jira_organization_key", this.selectedOrganization.trim());
    }

    return params.toString();
  }

  private buildTicketSlaExceptionPayload(
    jiraContext: JiraContext,
    ticket: TicketListIssue,
    status: "met" | "breached",
    reason: string
  ): TicketSlaExceptionUpdateRequestPayload {
    const payload: TicketSlaExceptionUpdateRequestPayload = {
      action: "update_ticket_sla_exception_customfields",
      jira_project_key: jiraContext.jiraProjectKeys,
      ticket_id: ticket.ticket_id,
    };

    if (this.selectedOrganization.trim()) {
      payload.jira_organization_key = this.selectedOrganization.trim();
    }

    if (
      this.metric === "resolution-breached" ||
      this.metric === "resolution-met"
    ) {
      payload.exception_resolution_status = status;
      payload.exception_resolution_update_reason = reason;
    } else if (
      this.metric === "response-breached" ||
      this.metric === "response-met"
    ) {
      payload.exception_response_status = status;
      payload.exception_response_update_reason = reason;
    }

    return payload;
  }

  private buildTicketSlaExceptionQueryString(
    payload: TicketSlaExceptionUpdateRequestPayload
  ): string {
    const params = new URLSearchParams();

    params.set("action", payload.action);
    params.set("jira_project_key", JSON.stringify(payload.jira_project_key));
    params.set("ticket_id", payload.ticket_id);

    if (payload.jira_organization_key?.trim()) {
      params.set("jira_organization_key", payload.jira_organization_key);
    }

    if (payload.exception_resolution_status) {
      params.set(
        "exception_resolution_status",
        payload.exception_resolution_status
      );
    }

    if (payload.exception_response_status) {
      params.set(
        "exception_response_status",
        payload.exception_response_status
      );
    }

    if (payload.exception_resolution_update_reason) {
      params.set(
        "exception_resolution_update_reason",
        payload.exception_resolution_update_reason
      );
    }

    if (payload.exception_response_update_reason) {
      params.set(
        "exception_response_update_reason",
        payload.exception_response_update_reason
      );
    }

    return params.toString();
  }

  private getSupportJiraApiUrl(queryString: string): string {
    return `${environment.apiURL}/${this.urlPrefix}/apis/support/jira?${queryString}`;
  }

  private getRequestHeaders(): { Authorization: string | null } {
    return { Authorization: localStorage.getItem("t") };
  }

  private getJiraContext(): JiraContext | null {
    const selectedAccount = this.fetchSelectedAccountDetails();
    const jiraOrganizationKey = selectedAccount?.jira_org_name?.trim();
    const jiraProjectKey = selectedAccount?.jira_project_key?.trim();
    const jiraProjectKeys = normalizeProjectKeys(jiraProjectKey);

    if (!jiraProjectKey || !jiraProjectKeys.length) {
      return null;
    }

    return {
      jiraOrganizationKey: jiraOrganizationKey || "",
      jiraProjectKey,
      jiraProjectKeys,
    };
  }

  private fetchSelectedAccountDetails(): any | null {
    const selectedAccounts = localStorage.getItem("accountIdData");
    if (!selectedAccounts || !this.accountId) {
      return null;
    }

    try {
      const parsedAccounts = JSON.parse(selectedAccounts);
      return (
        parsedAccounts?.list?.find((account: any) => {
          return account.accountId == this.accountId;
        }) || null
      );
    } catch {
      return null;
    }
  }

  private resetPaginationState(): void {
    this.currentPage = 1;
    this.tickets = [];
    this.totalTickets = 0;
    this.nextToken = null;
    this.errorMessage = null;
    this.pageCache = {};
    this.pageRequestTokens = { 1: null };
    this.pageNextTokens = {};
  }

  private resetExceptionForm(): void {
    this.exceptionFormTicket = null;
    this.exceptionStatus = "met";
    this.exceptionReason = "";
    this.exceptionReasonError = null;
  }

  private resetBreachedExceptionForm(): void {
    this.breachedExceptionFormTicket = null;
    this.breachedReason = "";
    this.breachedReasonError = null;
  }

  private isDateRangeValid(): boolean {
    if (!this.startDate || !this.endDate) {
      this.notifier.alert(
        "Info",
        "",
        "Please select both start date and end date.",
        "info",
        5000
      );
      return false;
    }

    if (this.startDate > this.endDate) {
      this.notifier.alert(
        "Info",
        "",
        "Start date should be less than or equal to end date.",
        "info",
        5000
      );
      return false;
    }

    return true;
  }

  private normalizeMetric(metric: string | null): SupportDashboardDetailMetric {
    if (metric && metric in this.metricTitles) {
      return metric as SupportDashboardDetailMetric;
    }

    return "total-tickets";
  }

  private getSlaDisplayName(
    slaName: "time_to_resolution" | "time_to_first_response"
  ): string {
    return slaName === "time_to_resolution"
      ? "Resolution SLA"
      : "Response SLA";
  }
}
